import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

const COPIED_RESET_MS = 2000;

interface UseCopyToClipboardResult {
    copy: (text: string) => Promise<void>;
    isCopied: boolean;
}

/**
 * Copies text to the clipboard and reports the result with a toast.
 * The isCopied flag stays true for two seconds so buttons can show a "Copied" state.
 */
export function useCopyToClipboard(
    successMessage: string = 'Copied to clipboard'
): UseCopyToClipboardResult {
    const [isCopied, setIsCopied] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const copy = useCallback(
        async (text: string) => {
            try {
                await navigator.clipboard.writeText(text);

                setIsCopied(true);
                toast.success(successMessage);

                if (timerRef.current !== null) {
                    clearTimeout(timerRef.current);
                }
                timerRef.current = setTimeout(() => {
                    setIsCopied(false);
                    timerRef.current = null;
                }, COPIED_RESET_MS);
            } catch {
                // Clipboard access can be blocked by the browser or by a missing permission.
                setIsCopied(false);
                toast.error('Could not copy. Select the text and copy it manually.');
            }
        },
        [successMessage]
    );

    return { copy, isCopied };
}
